import {
    getNationBuilderBrief,
    getNationBuilderBriefVariables,
} from '__generated__/getNationBuilderBrief';
import { NationBuilderBriefFields } from '__generated__/NationBuilderBriefFields';
import { AuthConsumer } from 'projects/components/AuthContext';
import DesignerCardContext from 'projects/components/DesignerCardContext';
import DesignerMetaContext from 'projects/components/DesignerMetaContext';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesigners from 'projects/routes/InviteDesignersPage';
import { createStepState, StepConfiguration } from 'projects/util/step';
import React from 'react';
import { useApolloClient } from 'react-apollo';
import { Route, Switch, useRouteMatch } from 'react-router';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import queries from './NationBuilder.graphql';
import SummaryPage from './SummaryPage';
import TimeAndBudgetPage from './TimeAndBudgetPage';

interface Props {
    briefId: string;
    step: string;
}

const steps: StepConfiguration[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
    },
    { step: 2, title: __('Your time & budget') },
    { step: 3, title: __('About you') },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        requiresAuth: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export function NationBuilder() {
    const route = useRouteMatch<Props>();
    const client = useApolloClient();

    return (
        <BriefQuery<getNationBuilderBrief, getNationBuilderBriefVariables>
            query={queries.getNationBuilderBrief}
            variables={{ id: route!.params.briefId }}
            fieldsTypename="NationBuilderBriefFields"
        >
            {(data, refetch) => {
                const { viewer } = data;
                const brief = {
                    ...data.brief,
                    fields: data.brief.fields as NationBuilderBriefFields,
                };

                const step = createStepState({
                    steps,
                    brief: data.brief,
                    currentStep: parseInt(route!.params.step, 10),
                    designerSources: data.brief.designersWithSource.map(d => d.source),
                });

                return (
                    <AuthConsumer>
                        {({ login }) => (
                            <Brief
                                refetch={refetch}
                                schema="nationBuilder"
                                brief={brief}
                                viewer={viewer}
                                login={login}
                                step={step}
                                client={client}
                            >
                                {form => (
                                    <Switch>
                                        <Route
                                            path={`/briefs/nationbuilder/:briefId/1`}
                                            render={() => <DetailsPage form={form} brief={brief} />}
                                        />
                                        <Route
                                            path={`/briefs/nationbuilder/:briefId/2`}
                                            render={() => (
                                                <TimeAndBudgetPage
                                                    form={form}
                                                    brief={brief}
                                                    site={data.site}
                                                />
                                            )}
                                        />
                                        <Route
                                            path={`/briefs/nationbuilder/:briefId/3`}
                                            render={() => (
                                                <AboutYouPage form={form} brief={brief} />
                                            )}
                                        />
                                        <Route
                                            path={`/briefs/nationbuilder/:briefId/4`}
                                            render={() => <SummaryPage brief={brief} />}
                                        />
                                        <Route
                                            path={`/briefs/nationbuilder/:briefId/5`}
                                            render={() => (
                                                <DesignerMetaContext.Provider
                                                    value={{
                                                        hasMoreResultsUrl: false,
                                                    }}
                                                >
                                                    <DesignerCardContext.Provider
                                                        value={{
                                                            highlightNewDesigners: false,
                                                            displayStatsTable: false,
                                                        }}
                                                    >
                                                        <InviteDesigners
                                                            brief={brief}
                                                            viewer={data.viewer}
                                                        />
                                                    </DesignerCardContext.Provider>
                                                </DesignerMetaContext.Provider>
                                            )}
                                        />
                                    </Switch>
                                )}
                            </Brief>
                        )}
                    </AuthConsumer>
                );
            }}
        </BriefQuery>
    );
}
